import { mulberry32 } from "./random";

import type { BrushParams } from "./types";

export const TIP_CACHE_MAX = 64;

const cache = new Map<string, HTMLCanvasElement>();

export const clearTipCache = () => cache.clear();

/** bucket size so zoom changes reuse nearby tips */
const bucket = (sizePx: number) =>
  Math.max(4, Math.min(256, Math.pow(2, Math.ceil(Math.log2(sizePx)))));

export const tipCacheKey = (
  params: BrushParams,
  sizeBucket: number,
  color: string,
  seed: number,
) =>
  `${params.tip}:${params.hardness}:${params.grainIntensity}:${params.strandCount}:${params.strandCurl}:${params.fuzz}:${sizeBucket}:${color}:${seed}`;

// lazily-created 1x1 canvas used only to let the browser normalize an
// arbitrary CSS color string (#fff, #rrggbbaa, named colors, ...) into a
// form we can reliably parse — never attached to the DOM.
let normalizeCtx: CanvasRenderingContext2D | null | undefined;
const getNormalizeCtx = (): CanvasRenderingContext2D | null => {
  if (normalizeCtx === undefined) {
    normalizeCtx =
      typeof document === "undefined"
        ? null
        : document.createElement("canvas").getContext("2d");
  }
  return normalizeCtx;
};

const HEX6_RE = /^#([0-9a-f]{6})$/i;
const RGB_RE = /^rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/i;

const parseHex6 = (color: string): string | null => {
  const m = HEX6_RE.exec(color);
  if (!m) {
    return null;
  }
  const n = parseInt(m[1], 16);
  return `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`;
};

const parseRgb = (color: string): string | null => {
  const m = RGB_RE.exec(color);
  if (!m) {
    return null;
  }
  return `${Math.round(Number(m[1]))},${Math.round(Number(m[2]))},${Math.round(
    Number(m[3]),
  )}`;
};

/** Normalizes any CSS color string to an "r,g,b" triple. Exported for tests. */
export const normalizeColorToRgb = (color: string): string => {
  // fast path: already a 6-digit hex, no canvas round-trip needed
  const direct = parseHex6(color);
  if (direct) {
    return direct;
  }
  const ctx = getNormalizeCtx();
  if (ctx) {
    try {
      ctx.fillStyle = "#000"; // reset so a bad `color` can't reuse a stale value
      ctx.fillStyle = color;
      const normalized = ctx.fillStyle;
      // jsdom/vitest-canvas-mock may not actually normalize — tolerate that
      // by falling through the chain below instead of trusting it blindly.
      const fromHex = parseHex6(normalized);
      if (fromHex) {
        return fromHex;
      }
      const fromRgb = parseRgb(normalized);
      if (fromRgb) {
        return fromRgb;
      }
    } catch {
      // fall through to genuinely-unparseable fallback below
    }
  }
  return "0,0,0";
};

const drawRound = (
  ctx: CanvasRenderingContext2D,
  r: number,
  color: string,
  hardness: number,
) => {
  const g = ctx.createRadialGradient(r, r, 0, r, r, r);
  g.addColorStop(0, color);
  g.addColorStop(Math.max(0.01, Math.min(1, hardness)), color);
  g.addColorStop(
    1,
    "rgba(0,0,0,0)".replace("0,0,0", normalizeColorToRgb(color)),
  );
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, r * 2, r * 2);
};

const drawGrain = (
  ctx: CanvasRenderingContext2D,
  r: number,
  color: string,
  intensity: number,
  rng: () => number,
) => {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(r, r, r, 0, Math.PI * 2);
  ctx.fill();
  // punch speckle holes — this is what reads as graphite/charcoal grain
  ctx.globalCompositeOperation = "destination-out";
  const holes = Math.floor(r * r * intensity * 1.2);
  for (let i = 0; i < holes; i++) {
    const a = rng() * Math.PI * 2;
    const d = Math.sqrt(rng()) * r;
    const hr = 0.4 + rng() * r * 0.14;
    ctx.globalAlpha = 0.35 + rng() * 0.55;
    ctx.beginPath();
    ctx.arc(r + Math.cos(a) * d, r + Math.sin(a) * d, hr, 0, Math.PI * 2);
    ctx.fill();
  }
  // soften the disc edge
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = "destination-in";
  const g = ctx.createRadialGradient(r, r, 0, r, r, r);
  g.addColorStop(0.7, "rgba(0,0,0,1)");
  g.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, r * 2, r * 2);
  ctx.globalCompositeOperation = "source-over";
};

/** thin curved strands; curl bows them, fuzz adds stray low-alpha strands
 *  OUTSIDE the core radius — the felted-wool edge from the reference art */
const drawStrands = (
  ctx: CanvasRenderingContext2D,
  r: number,
  color: string,
  params: BrushParams,
  rng: () => number,
) => {
  const count = Math.max(3, params.strandCount);
  ctx.strokeStyle = color;
  ctx.lineCap = "round";
  const strand = (cx: number, cy: number, reach: number, alpha: number) => {
    const a0 = rng() * Math.PI * 2;
    const len = reach * (0.6 + rng() * 0.8);
    const curl = params.strandCurl;
    const x0 = cx + Math.cos(a0) * len * 0.5;
    const y0 = cy + Math.sin(a0) * len * 0.5;
    const x1 = cx - Math.cos(a0) * len * 0.5;
    const y1 = cy - Math.sin(a0) * len * 0.5;
    // control point off-axis by curl — 0 straight bristle, 1 tight loop
    const midx = (x0 + x1) / 2 + (rng() * 2 - 1) * curl * len;
    const midy = (y0 + y1) / 2 + (rng() * 2 - 1) * curl * len;
    ctx.globalAlpha = alpha;
    ctx.lineWidth = Math.max(0.5, r * 0.06 * (0.5 + rng()));
    ctx.beginPath();
    ctx.moveTo(x0, y0);
    ctx.quadraticCurveTo(midx, midy, x1, y1);
    ctx.stroke();
  };
  for (let i = 0; i < count; i++) {
    // core strands, clustered toward the middle
    const d = Math.sqrt(rng()) * r * 0.65;
    const a = rng() * Math.PI * 2;
    strand(
      r + Math.cos(a) * d,
      r + Math.sin(a) * d,
      r * 0.8,
      0.5 + rng() * 0.5,
    );
  }
  const fuzzCount = Math.floor(count * params.fuzz);
  for (let i = 0; i < fuzzCount; i++) {
    // stray fuzz strands outside the core
    const d = r * (0.7 + rng() * 0.5);
    const a = rng() * Math.PI * 2;
    strand(
      r + Math.cos(a) * d,
      r + Math.sin(a) * d,
      r * 0.7,
      0.08 + rng() * 0.12,
    );
  }
  ctx.globalAlpha = 1;
};

const drawSpray = (
  ctx: CanvasRenderingContext2D,
  r: number,
  color: string,
  hardness: number,
  rng: () => number,
) => {
  ctx.fillStyle = color;
  const dots = Math.floor(r * r * 0.5);
  for (let i = 0; i < dots; i++) {
    const a = rng() * Math.PI * 2;
    const d = Math.pow(rng(), 0.5 + hardness) * r;
    ctx.globalAlpha = 0.25 + rng() * 0.5;
    const dr = 0.4 + rng() * r * 0.06;
    ctx.beginPath();
    ctx.arc(r + Math.cos(a) * d, r + Math.sin(a) * d, dr, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
};

export const getTipCanvas = (
  params: BrushParams,
  sizePx: number,
  color: string,
  seed: number,
): HTMLCanvasElement | null => {
  if (typeof document === "undefined") {
    return null;
  }
  const sz = bucket(sizePx);
  const key = tipCacheKey(params, sz, color, seed);
  const hit = cache.get(key);
  if (hit) {
    // refresh LRU position
    cache.delete(key);
    cache.set(key, hit);
    return hit;
  }
  const canvas = document.createElement("canvas");
  // fiber tips draw fuzz outside the core — give them margin
  const margin = params.tip === "fiber" ? 2.4 : 1;
  canvas.width = canvas.height = Math.ceil(sz * 2 * margin);
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return null;
  }
  const r = sz;
  ctx.save();
  ctx.translate((canvas.width - sz * 2) / 2, (canvas.height - sz * 2) / 2);
  const rng = mulberry32(seed);
  try {
    switch (params.tip) {
      case "round":
        drawRound(ctx, r, color, params.hardness);
        break;
      case "grain":
        drawGrain(ctx, r, color, params.grainIntensity, rng);
        break;
      case "bristle":
      case "fiber":
        drawStrands(ctx, r, color, params, rng);
        if (params.grainIntensity > 0) {
          // weave texture on top (cloth)
          ctx.globalCompositeOperation = "destination-out";
          const holes = Math.floor(r * r * params.grainIntensity * 0.4);
          for (let i = 0; i < holes; i++) {
            ctx.globalAlpha = 0.3 * rng();
            ctx.fillRect(rng() * r * 2, rng() * r * 2, 1.5, 1.5);
          }
          ctx.globalAlpha = 1;
          ctx.globalCompositeOperation = "source-over";
        }
        break;
      case "spray":
        drawSpray(ctx, r, color, params.hardness, rng);
        break;
    }
  } catch {
    // never a broken stroke — fall back to a plain disc
    ctx.globalCompositeOperation = "source-over";
    ctx.globalAlpha = 1;
    ctx.clearRect(
      -canvas.width,
      -canvas.height,
      canvas.width * 2,
      canvas.height * 2,
    );
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(r, r, r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
  cache.set(key, canvas);
  if (cache.size > TIP_CACHE_MAX) {
    cache.delete(cache.keys().next().value!);
  }
  return canvas;
};
